import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { findMenuItem, groupOf } from "../components/AE2CNavbar";
import { PAGES } from "./pageConfig";
import { useApi } from "../api";

/* ───────── helpers ───────── */
const NUMERIC = /amount|price|total|balance|debit|credit|revenue|value|float|commission|qty|expected|counted|difference|variance|margin|inflows|outflows|net|cost|transactions|items|basket|change|in stock|level|sales$/i;
const today = () => new Date().toISOString().slice(0, 10);
const monthStart = () => today().slice(0, 8) + "01";

export { groupOf };

export function PageHeader({ slug, actions }) {
  const item = findMenuItem(slug) || { label: slug, icon: "fa-file" };
  const cfg = PAGES[slug] || {};
  const group = groupOf(slug);
  return (
    <>
      <nav className="crumbs" aria-label="Breadcrumb">
        <Link to="/dashboard">Home</Link>
        {group && (<><i className="fas fa-chevron-right" /><span>{group}</span></>)}
        <i className="fas fa-chevron-right" /><span aria-current="page">{item.label}</span>
      </nav>
      <div className="pg-head">
        <div className="pg-title">
          <span className="chip"><i className={`fas ${item.icon} ${item.color || ""}`} /></span>
          <div>
            <h1>{item.label}</h1>
            {cfg.purpose && <p>{cfg.purpose}</p>}
          </div>
        </div>
        {actions && <div className="no-print" style={{ display: "flex", gap: ".5rem" }}>{actions}</div>}
      </div>
    </>
  );
}

/* ───────── table / tool body ───────── */
function DataTable({ slug, cfg }) {
  const api = useApi();
  const [from, setFrom] = useState(monthStart());
  const [to, setTo] = useState(today());
  const [q, setQ] = useState("");
  const [rows, setRows] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback((params) => {
    setRows(null); setError(null);
    api.get(`/api/pages/${slug}`, params)
      .then((d) => setRows(d.rows || []))
      .catch((e) => { setError(e.message); setRows([]); });
  }, [api, slug]);

  useEffect(() => {
    load(cfg.kind === "tool" || cfg.noDates ? {} : { from: monthStart(), to: today() });
  }, [load, cfg]);

  const submit = (e) => {
    e.preventDefault();
    load(cfg.kind === "tool" ? { q } : cfg.noDates ? {} : { from, to });
  };

  const showToolbar = cfg.kind === "tool" || !cfg.noDates;

  return (
    <div className="panel">
      {showToolbar && (
        <form className="toolbar" onSubmit={submit}>
          {cfg.kind === "tool" ? (
            <div className="field grow">
              <label htmlFor="q">{cfg.search}</label>
              <div className="search">
                <i className="fas fa-magnifying-glass" />
                <input id="q" value={q} onChange={(e) => setQ(e.target.value)} autoFocus autoComplete="off" />
              </div>
            </div>
          ) : (
            <>
              <div className="field"><label htmlFor="from">From</label><input id="from" type="date" value={from} onChange={(e) => setFrom(e.target.value)} /></div>
              <div className="field"><label htmlFor="to">To</label><input id="to" type="date" value={to} onChange={(e) => setTo(e.target.value)} /></div>
            </>
          )}
          <button type="submit" className="btn primary">{cfg.kind === "tool" ? "Search" : "Show results"}</button>
        </form>
      )}

      <div className="tbl-wrap">
        <table className="tbl">
          <thead>
            <tr>{cfg.columns.map((c) => <th key={c} className={NUMERIC.test(c) ? "num" : ""}>{c}</th>)}</tr>
          </thead>
          <tbody>
            {rows === null &&
              [0, 1, 2].map((r) => (
                <tr key={r}>{cfg.columns.map((c) => <td key={c}><div className="skel" /></td>)}</tr>
              ))}
            {rows?.map((row, i) => (
              <tr key={i}>
                {cfg.columns.map((c, j) => (
                  <td key={c} className={NUMERIC.test(c) ? "num mono" : ""}>
                    {typeof row[j] === "number" ? row[j].toLocaleString("en-US") : row[j]}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        {rows?.length === 0 && (
          <div className="empty">
            <i className={`fas ${error ? "fa-plug-circle-xmark" : "fa-inbox"}`} />
            {error
              ? <>Couldn't load data: {error}. Check that the Flask server is running.</>
              : <>No records found. Rows appear here once <code>/api/pages/{slug}</code> returns data.</>}
          </div>
        )}
      </div>
    </div>
  );
}

/* ───────── form body ───────── */
function DataForm({ slug, cfg }) {
  const api = useApi();
  const blank = Object.fromEntries(cfg.fields.map((f) => [f.name, f.type === "select" ? f.options[0] : ""]));
  const [values, setValues] = useState(blank);
  const [status, setStatus] = useState(null); // { tone: "ok" | "warn" | "err", text }
  const [busy, setBusy] = useState(false);

  const set = (name) => (e) => setValues((v) => ({ ...v, [name]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setStatus(null);
    try {
      const res = await api.post(`/api/pages/${slug}`, values);
      if (res.saved === false) {
        setStatus({ tone: "warn", text: res.message });
      } else {
        setStatus({ tone: "ok", text: res.message || cfg.done });
        setValues(blank);
      }
    } catch (err) {
      setStatus({ tone: "err", text: `Not saved: ${err.message}` });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="panel">
      <form className="form" onSubmit={submit}>
        {cfg.fields.map((f) => {
          const common = { id: f.name, name: f.name, value: values[f.name], onChange: set(f.name), required: f.required };
          return (
            <div key={f.name} className={`field ${f.type === "textarea" ? "wide" : ""}`}>
              <label htmlFor={f.name}>{f.label}{f.required && <span className="req" aria-hidden="true">*</span>}</label>
              {f.type === "textarea" ? (
                <textarea {...common} maxLength={f.maxLength} />
              ) : f.type === "select" ? (
                <select {...common}>{f.options.map((o) => <option key={o}>{o}</option>)}</select>
              ) : (
                <input {...common} type={f.type} min={f.min} step={f.step} placeholder={f.placeholder} autoComplete={f.autoComplete} />
              )}
              {f.maxLength && <span className="hint">{values[f.name].length} / {f.maxLength} characters</span>}
            </div>
          );
        })}
        <div className="form-actions">
          <button type="submit" className="btn primary" disabled={busy}>{busy ? "Saving…" : cfg.submit}</button>
          {status && <span className={`msg ${status.tone}`} role="status">{status.text}</span>}
        </div>
      </form>
    </div>
  );
}

/* ───────── page ───────── */
export default function GenericPage({ slug }) {
  const cfg = PAGES[slug];
  // A page marked "custom" (or with nothing to draw) has its own screen; reaching here means that screen
  // isn't registered - usually stale files from unzipping a new version over an old folder.
  if (!cfg || (cfg.kind !== "form" && !Array.isArray(cfg.columns)) || (cfg.kind === "form" && !Array.isArray(cfg.fields))) {
    return (
      <main className="pg">
        <PageHeader slug={slug} />
        <div className="ui-notice warn" style={{ display: "block" }}>
          This page's screen couldn't be loaded. If you unzipped a new version over an old folder, delete the
          folder's <code>src</code> directory and extract the new version fresh, then restart <code>npm start</code>.
        </div>
      </main>
    );
  }
  const actions = cfg?.print ? (
    <button type="button" className="btn" onClick={() => window.print()}><i className="fas fa-print" /> Print</button>
  ) : null;

  return (
    <main className="pg">
      <PageHeader slug={slug} actions={actions} />
      {cfg.kind === "form" ? <DataForm slug={slug} cfg={cfg} /> : <DataTable slug={slug} cfg={cfg} />}
    </main>
  );
}
